import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, Wallet, LogOut, ShieldCheck, Loader2,
  BanknoteIcon, Smartphone, Clock, CheckCircle2, XCircle,
  Users, Copy, Check, Cpu,
} from "lucide-react";
import { toast } from "../components/ui/sonner";
import { useMobileAuth } from "../context/MobileAuthContext";

const MIN = 100;

function formatINR(n) {
  const v = Number(n || 0);
  return "₹" + v.toLocaleString("en-IN", { maximumFractionDigits: 2 });
}

function ReferralCard({ referralId, referralEarnings, referralCount }) {
  const [copied, setCopied] = useState(false);
  const shareUrl = referralId ? `https://geonetix.live/?ref=${referralId}` : "";
  const canShare = Boolean(referralId);

  const copy = async () => {
    if (!canShare) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast("Referral link copied!", { description: shareUrl });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast("Couldn't copy — long-press and copy manually.");
    }
  };

  return (
    <div
      data-testid="referral-card"
      className="relative overflow-hidden rounded-3xl border border-[#00B4D8]/30 bg-gradient-to-br from-[#0f2b4a] to-[#112240] p-7"
    >
      <div className="absolute -bottom-16 -left-16 h-48 w-48 rounded-full bg-[#00B4D8]/15 blur-3xl" />
      <div className="relative flex items-center gap-2 text-[#00B4D8] mb-3">
        <Users size={18} />
        <span className="font-mono-gx text-xs uppercase tracking-[0.2em]">Referrals</span>
      </div>
      <div className="relative grid grid-cols-2 gap-4 mb-5">
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Earned</div>
          <div data-testid="referral-earnings" className="text-2xl font-black font-mono-gx text-[#4CAF50]">
            {formatINR(referralEarnings)}
          </div>
        </div>
        <div>
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Total Referrals</div>
          <div data-testid="referral-count" className="text-2xl font-black font-mono-gx text-white">
            {referralCount ?? 0}
          </div>
        </div>
      </div>
      {canShare ? (
        <div className="relative">
          <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Your invite link</div>
          <div className="flex items-stretch gap-2">
            <div
              data-testid="referral-link"
              className="flex-1 truncate rounded-xl border border-white/10 bg-black/30 px-3 py-2.5 text-xs text-slate-300 font-mono-gx"
              title={shareUrl}
            >
              {shareUrl}
            </div>
            <button
              type="button"
              data-testid="referral-share-btn"
              onClick={copy}
              className="inline-flex items-center justify-center gap-1.5 rounded-xl bg-[#00B4D8] px-4 text-xs font-semibold text-[#04140a] transition-[filter,transform] duration-200 hover:brightness-110 hover:-translate-y-0.5"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
          <p className="mt-2 text-[11px] text-slate-500">
            Share this link — you earn ₹ every time a friend joins and starts mapping.
          </p>
        </div>
      ) : (
        <p className="relative text-xs text-slate-500">Referral link will appear here once your account activates it.</p>
      )}
    </div>
  );
}

function looksLikeImage(v) {
  if (typeof v !== "string") return false;
  const s = v.trim();
  if (!s) return false;
  if (s.startsWith("data:image/")) return true;
  // http(s) URL ending with an image extension (query strings allowed).
  if (/^https?:\/\/[^\s]+\.(jpe?g|png|gif|webp|svg|bmp|heic|heif)(\?[^\s]*)?$/i.test(s)) return true;
  // Long standalone base64 blob (no whitespace, valid charset).
  if (s.length > 200 && /^[A-Za-z0-9+/=_-]+$/.test(s) && !s.includes(" ")) return true;
  return false;
}

function toImageSrc(v) {
  const s = String(v).trim();
  if (s.startsWith("data:image/") || /^https?:\/\//i.test(s)) return s;
  // Bare base64 blob — default to JPEG (safe for photos).
  return `data:image/jpeg;base64,${s}`;
}

function ImageThumb({ src, alt, testid }) {
  const [broken, setBroken] = useState(false);
  if (broken) {
    return <span className="text-xs text-slate-500 break-all">(image could not load)</span>;
  }
  return (
    <a
      href={src}
      target="_blank"
      rel="noreferrer"
      data-testid={`image-link-${testid}`}
      className="block"
    >
      <img
        data-testid={`image-${testid}`}
        src={src}
        alt={alt}
        loading="lazy"
        onError={() => setBroken(true)}
        className="max-h-48 w-full max-w-sm rounded-xl border border-white/10 object-cover"
      />
    </a>
  );
}

function DetailBlock({ title, icon: Icon, data, emptyHint }) {
  const entries = Object.entries(data || {});
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
      <div className="flex items-center gap-3 mb-4">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#00B4D8]/10 border border-[#00B4D8]/30">
          <Icon size={18} className="text-[#00B4D8]" />
        </div>
        <h3 className="font-display font-bold text-lg">{title}</h3>
      </div>
      {entries.length === 0 ? (
        <p className="text-sm text-slate-500">
          {emptyHint || `No ${title.toLowerCase()} found on your account.`}
        </p>
      ) : (
        <dl className="space-y-2.5">
          {entries.map(([k, v]) => {
            const isImg = looksLikeImage(v);
            const testid = k.replace(/\s+/g, "-").toLowerCase();
            return (
              <div
                key={k}
                className={
                  isImg
                    ? "flex flex-col gap-2 border-b border-white/5 pb-3 last:border-0"
                    : "flex justify-between gap-4 text-sm border-b border-white/5 pb-2 last:border-0"
                }
              >
                <dt className="text-slate-400 text-sm">{k}</dt>
                <dd className={isImg ? "" : "text-white text-right break-all text-sm"}>
                  {isImg ? (
                    <ImageThumb src={toImageSrc(v)} alt={k} testid={testid} />
                  ) : typeof v === "object" && v !== null ? (
                    <div className="space-y-0.5">
                      {Object.entries(v).map(([kk, vv]) => (
                        <div key={kk} className="text-xs">
                          <span className="text-slate-500">{kk}: </span>
                          {String(vv)}
                        </div>
                      ))}
                    </div>
                  ) : typeof v === "boolean" ? (
                    v ? "Yes" : "No"
                  ) : (
                    String(v)
                  )}
                </dd>
              </div>
            );
          })}
        </dl>
      )}
    </div>
  );
}

const statusStyles = {
  pending: { icon: Clock, cls: "text-amber-300 border-amber-300/40 bg-amber-300/10" },
  approved: { icon: CheckCircle2, cls: "text-[#4CAF50] border-[#4CAF50]/40 bg-[#4CAF50]/10" },
  rejected: { icon: XCircle, cls: "text-red-400 border-red-400/40 bg-red-400/10" },
};

export default function Dashboard() {
  const { profile, setProfile, loading, isAuthed, logout, authFetch } = useMobileAuth();
  const navigate = useNavigate();

  const [history, setHistory] = useState([]);
  const [method, setMethod] = useState("upi");
  const [amount, setAmount] = useState("");
  const [upiId, setUpiId] = useState("");
  const [bank, setBank] = useState({ account_name: "", account_number: "", ifsc: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !isAuthed) navigate("/login");
  }, [loading, isAuthed, navigate]);

  const loadHistory = useCallback(async () => {
    try {
      const data = await authFetch("/api/mobile/withdrawals");
      setHistory(data.withdrawals || []);
      setProfile((p) => {
        if (!p) return p;
        const next = { ...p };
        if (data.balance != null) next.balance = data.balance;
        if (data.earnings) next.earnings = data.earnings;
        return next;
      });
    } catch (e) {
      /* ignore */
    }
  }, [authFetch, setProfile]);

  useEffect(() => {
    if (isAuthed) loadHistory();
  }, [isAuthed, loadHistory]);

  const submitWithdrawal = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt < MIN) {
      toast(`Minimum withdrawal is ${formatINR(MIN)}.`);
      return;
    }
    if (profile && amt > profile.balance) {
      toast("Amount exceeds your available balance.");
      return;
    }
    setSubmitting(true);
    try {
      const body =
        method === "upi"
          ? { amount: amt, method: "upi", upi_id: upiId }
          : { amount: amt, method: "bank", ...bank };
      const data = await authFetch("/api/mobile/withdrawals", {
        method: "POST",
        body: JSON.stringify(body),
      });
      setProfile((p) => (p ? { ...p, balance: data.new_balance } : p));
      setAmount("");
      setUpiId("");
      setBank({ account_name: "", account_number: "", ifsc: "" });
      toast("Withdrawal requested!", {
        description: `${formatINR(amt)} is pending approval. It's been held from your balance.`,
      });
      loadHistory();
    } catch (err) {
      toast("Withdrawal failed", { description: err.message });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading || !profile) {
    return (
      <div className="min-h-screen bg-[#0A192F] flex items-center justify-center text-slate-300">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  const inputCls =
    "w-full bg-white/5 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder:text-slate-500 focus:outline-none focus:border-[#00B4D8]/60";

  return (
    <div data-testid="dashboard-page" className="min-h-screen bg-[#0A192F] text-slate-100 antialiased">
      <header className="sticky top-0 z-40 bg-black border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 md:px-8 h-[72px] flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src="/geonetix-logo-full.webp" alt="GeoNetix" className="h-10 md:h-12 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/" className="hidden sm:inline-flex items-center gap-2 text-sm text-slate-300 hover:text-[#00B4D8]">
              <ArrowLeft size={16} /> Home
            </Link>
            <button
              data-testid="dashboard-logout"
              onClick={() => { logout(); navigate("/"); }}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-4 py-2 text-sm font-semibold hover:border-red-400/60 hover:text-red-300 transition-colors"
            >
              <LogOut size={15} /> Log out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 md:px-8 py-10 md:py-14">
        <p className="text-slate-400 text-sm">Welcome back{profile.name ? "," : ""}</p>
        <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight mb-8">
          {profile.name || profile.email}
        </h1>

        <div className="grid lg:grid-cols-[1fr_1.1fr] gap-6">
          {/* Balance + withdraw */}
          <div className="space-y-6">
            <div className="relative overflow-hidden rounded-3xl border border-[#4CAF50]/30 bg-[#112240] p-7 gx-green-glow">
              <div className="absolute -top-16 -right-16 h-48 w-48 rounded-full bg-[#4CAF50]/20 blur-3xl" />
              <div className="relative flex items-center gap-2 text-[#4CAF50] mb-2">
                <Wallet size={18} />
                <span className="font-mono-gx text-xs uppercase tracking-[0.2em]">Available Balance</span>
              </div>
              <div data-testid="dashboard-balance" className="relative text-5xl font-black tracking-tight font-mono-gx">
                {formatINR(profile.balance)}
              </div>
              <p className="relative text-xs text-slate-400 mt-2">Withdraw to UPI or bank in INR.</p>

              {/* Withdrawal Progress Bar */}
              {(() => {
                const bal = Number(profile.balance || 0);
                const ready = bal >= MIN;
                const pct = ready ? 100 : Math.max(0, Math.min(100, (bal / MIN) * 100));
                const remaining = Math.max(0, MIN - bal);
                return (
                  <div data-testid="withdrawal-progress" className="relative mt-5">
                    <div className="flex items-center justify-between text-[11px] uppercase tracking-[0.18em] mb-1.5">
                      <span className="text-slate-500">Progress to ₹{MIN} minimum</span>
                      <span data-testid="withdrawal-progress-pct" className={ready ? "text-[#4CAF50] font-bold" : "text-slate-400 font-mono-gx"}>
                        {ready ? "READY" : `${pct.toFixed(0)}%`}
                      </span>
                    </div>
                    <div className="h-2 rounded-full bg-white/5 border border-white/10 overflow-hidden">
                      <div
                        data-testid="withdrawal-progress-fill"
                        className={`h-full transition-[width] duration-500 ${ready ? "bg-[#4CAF50]" : "bg-gradient-to-r from-[#00B4D8] to-[#4CAF50]"}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <p className="mt-1.5 text-[11px] text-slate-500">
                      {ready ? (
                        <span className="text-[#4CAF50]">You&apos;ve cleared the minimum — request a withdrawal any time.</span>
                      ) : (
                        <>Earn <span className="text-slate-300 font-mono-gx">{formatINR(remaining)}</span> more to unlock withdrawals.</>
                      )}
                    </p>
                  </div>
                );
              })()}

              {profile.earnings && (
                <div data-testid="earnings-breakdown" className="relative mt-5 grid grid-cols-2 gap-3 border-t border-white/10 pt-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Approved Mapping</div>
                    <div data-testid="earnings-mapping" className="text-sm font-semibold font-mono-gx text-slate-100">
                      {formatINR(profile.earnings.approved_mapping)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Approved Data</div>
                    <div data-testid="earnings-data" className="text-sm font-semibold font-mono-gx text-slate-100">
                      {formatINR(profile.earnings.approved_data)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Total Approved</div>
                    <div data-testid="earnings-total" className="text-sm font-semibold font-mono-gx text-[#4CAF50]">
                      {formatINR(profile.earnings.approved_total)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500">Pending Withdrawals</div>
                    <div data-testid="earnings-pending" className="text-sm font-semibold font-mono-gx text-amber-300">
                      {formatINR(profile.earnings.pending_withdrawals)}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <form onSubmit={submitWithdrawal} data-testid="withdrawal-form" className="rounded-3xl border border-white/12 bg-[#112240] p-7">
              <h3 className="font-display font-bold text-xl mb-5">Withdraw Earnings</h3>

              <div className="inline-flex rounded-full border border-white/10 bg-black/20 p-1 mb-5">
                <button
                  type="button"
                  data-testid="withdraw-method-upi"
                  onClick={() => setMethod("upi")}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${method === "upi" ? "bg-[#00B4D8] text-[#04140a]" : "text-slate-300"}`}
                >
                  <Smartphone size={15} /> UPI
                </button>
                <button
                  type="button"
                  data-testid="withdraw-method-bank"
                  onClick={() => setMethod("bank")}
                  className={`flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold transition-colors ${method === "bank" ? "bg-[#00B4D8] text-[#04140a]" : "text-slate-300"}`}
                >
                  <BanknoteIcon size={15} /> Bank Transfer
                </button>
              </div>

              <label className="block mb-4">
                <span className="mb-2 block text-sm text-slate-300">Amount (min {formatINR(MIN)})</span>
                <input
                  data-testid="withdraw-amount"
                  type="number"
                  min={MIN}
                  step="1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="500"
                  className={inputCls}
                />
              </label>

              {method === "upi" ? (
                <label className="block mb-5">
                  <span className="mb-2 block text-sm text-slate-300">UPI ID</span>
                  <input
                    data-testid="withdraw-upi"
                    value={upiId}
                    onChange={(e) => setUpiId(e.target.value)}
                    placeholder="name@bank"
                    className={inputCls}
                  />
                </label>
              ) : (
                <div className="space-y-3 mb-5">
                  <input data-testid="withdraw-acc-name" value={bank.account_name} onChange={(e) => setBank({ ...bank, account_name: e.target.value })} placeholder="Account holder name" className={inputCls} />
                  <input data-testid="withdraw-acc-number" value={bank.account_number} onChange={(e) => setBank({ ...bank, account_number: e.target.value })} placeholder="Account number" className={inputCls} />
                  <input data-testid="withdraw-ifsc" value={bank.ifsc} onChange={(e) => setBank({ ...bank, ifsc: e.target.value.toUpperCase() })} placeholder="IFSC code" className={inputCls} />
                </div>
              )}

              <button
                data-testid="withdraw-submit"
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2.5 rounded-full bg-[#4CAF50] px-7 py-4 text-[15px] font-semibold text-[#04140a] hover:brightness-110 transition-[filter,transform] duration-200 hover:-translate-y-0.5 gx-green-glow disabled:opacity-60"
              >
                {submitting ? <Loader2 size={18} className="animate-spin" /> : <Wallet size={18} />}
                {submitting ? "Requesting..." : "Request Withdrawal"}
              </button>
              <p className="mt-3 text-xs text-slate-500">
                The amount is held from your balance immediately and refunded if the request is rejected.
              </p>
            </form>
          </div>

          {/* Details + history */}
          <div className="space-y-6">
            <ReferralCard
              referralId={profile.account?.["Referral ID"]}
              referralEarnings={profile.account?.["Referral Earnings"] ?? 0}
              referralCount={profile.account?.["Referral Count"] ?? 0}
            />
            <DetailBlock title="Device Details" icon={Cpu} data={profile.vehicle} emptyHint="Pair your GeoNetix dashcam in the mobile app to link your device." />
            <DetailBlock title="KYC Details" icon={ShieldCheck} data={profile.kyc} emptyHint="Submit your KYC in the mobile app under Profile → KYC. It'll appear here once verified." />
            <DetailBlock
              title="Account & Earnings"
              icon={Wallet}
              data={Object.fromEntries(
                Object.entries(profile.account || {}).filter(
                  ([k]) => !["Referral ID", "Referral Count", "Referral Earnings"].includes(k)
                )
              )}
            />

            <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6">
              <h3 className="font-display font-bold text-lg mb-4">Withdrawal History</h3>
              {history.length === 0 ? (
                <p className="text-sm text-slate-500" data-testid="withdrawal-history-empty">No withdrawals yet.</p>
              ) : (
                <ul className="space-y-3" data-testid="withdrawal-history">
                  {history.map((w) => {
                    const st = String(w.status || "pending").toLowerCase();
                    const s = statusStyles[st] || statusStyles.pending;
                    const SIcon = s.icon;
                    return (
                      <li key={w.id} className="flex items-center justify-between gap-3 border-b border-white/5 pb-3 last:border-0">
                        <div>
                          <div className="font-semibold font-mono-gx">{formatINR(w.amount)}</div>
                          <div className="text-xs text-slate-500 uppercase">{w.method || (w.upi_id ? "upi" : "bank")}</div>
                        </div>
                        <span className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${s.cls}`}>
                          <SIcon size={13} /> {st}
                        </span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
